import apiService from './apiService';
import { resolveMediaUrl } from '../utils/imageUrl';
import { extractYouTubeId } from '../utils/videoUtils';

const CATEGORY_PRESETS = {
    storefront: {
        cardLabel: 'Storefront',
        tabLabel: 'Storefront & Windows',
    },
    cooling: {
        cardLabel: 'Cooling & Freezers',
        tabLabel: 'Refrigerators & Freezers',
    },
    tvs: {
        cardLabel: 'LED TVs & Audio',
        tabLabel: 'Smart TVs & Sound',
    },
    ac: {
        cardLabel: 'Inverter ACs',
        tabLabel: 'Air Conditioners',
    },
    washing: {
        cardLabel: 'Washing & Laundry',
        tabLabel: 'Washing Machines',
    },
    kitchen: {
        cardLabel: 'Kitchen Appliances',
        tabLabel: 'Kitchen & Small Appliances',
    },
    showroom: {
        cardLabel: 'Showroom',
        tabLabel: 'Showroom Floor',
    },
};

const CATEGORY_ALIAS_MAP = {
    'storefront': 'storefront',
    'storefront-windows': 'storefront',
    'storefront-and-windows': 'storefront',
    'cooling': 'cooling',
    'cooling-freezers': 'cooling',
    'refrigerators-freezers': 'cooling',
    'refrigerators-and-freezers': 'cooling',
    'tvs': 'tvs',
    'led-tvs-audio': 'tvs',
    'smart-tvs-sound': 'tvs',
    'smart-tvs-and-sound': 'tvs',
    'ac': 'ac',
    'air-conditioners': 'ac',
    'inverter-acs': 'ac',
    'washing': 'washing',
    'washing-machines': 'washing',
    'washing-laundry': 'washing',
    'kitchen': 'kitchen',
    'kitchen-appliances': 'kitchen',
    'showroom': 'showroom',
    'showroom-floor': 'showroom',
    'general': 'showroom',
};

function extractUrlString(value) {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'object') {
        const candidate = value.url || value.secure_url || value.path || value.src || '';
        return typeof candidate === 'string' ? candidate.trim() : '';
    }
    return '';
}

function hasImageExtension(url) {
    if (!url || typeof url !== 'string') return false;
    const clean = url.trim().split(/[?#]/)[0];
    return /\.(jpe?g|png|webp|gif|avif|svg|bmp|heic|heif)$/i.test(clean);
}

function hasVideoExtension(url) {
    if (!url || typeof url !== 'string') return false;
    const clean = url.trim().split(/[?#]/)[0];
    return /\.(mp4|webm|mov|m4v|ogg|ogv|mkv|avi)$/i.test(clean);
}

function isKnownEmbedUrl(url) {
    if (!url || typeof url !== 'string') return false;
    const lower = url.toLowerCase();
    return (
        lower.includes('player.vimeo.com/') ||
        lower.includes('tiktok.com/embed') ||
        lower.includes('facebook.com/plugins/video') ||
        lower.includes('dailymotion.com/embed') ||
        (lower.includes('instagram.com/') && lower.includes('/embed'))
    );
}

function formatSlugLabel(slug) {
    if (!slug) return 'Showroom';
    return String(slug)
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, (ch) => ch.toUpperCase())
        .trim();
}

/**
 * Ecommerce On-Ground Media Service
 * Fetches published media items for the "AUTHENTIC ON-GROUND MEDIA" section
 * from the public /api/ecommerce/on-ground-media endpoint.
 */
class OnGroundMediaService {
    /**
     * Normalize category slug and human-readable labels for cards and filter tabs.
     */
    resolveCategoryMeta(item) {
        const rawCategory = String(
            item.category ||
                item.category_key ||
                item.categoryKey ||
                item.filter_category ||
                item.filterCategory ||
                ''
        ).trim();

        const rawCardLabel = String(
            item.category_label || item.categoryLabel || ''
        ).trim();

        const rawTabLabel = String(
            item.category_tab_label || item.categoryTabLabel || ''
        ).trim();

        const slugCandidate = (rawCategory || rawCardLabel || 'showroom')
            .toLowerCase()
            .replace(/&/g, 'and')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');

        const categoryKey =
            CATEGORY_ALIAS_MAP[slugCandidate] || slugCandidate || 'showroom';
        const preset = CATEGORY_PRESETS[categoryKey] || null;

        const categoryLabel =
            rawCardLabel ||
            (preset ? preset.cardLabel : '') ||
            (rawCategory && rawCategory.length <= 36 ? rawCategory : formatSlugLabel(categoryKey));

        const categoryTabLabel =
            rawTabLabel ||
            (preset ? preset.tabLabel : '') ||
            rawCardLabel ||
            categoryLabel;

        return {
            category: categoryKey,
            categoryLabel,
            categoryTabLabel,
        };
    }

    /**
     * Normalize a single raw media record from the backend API.
     * Supports both images and videos (uploaded files or external URLs).
     *
     * @param {object} item
     * @param {number} index
     * @returns {object|null}
     */
    normalizeMediaItem(item, index = 0) {
        if (!item || typeof item !== 'object') return null;

        const rawStatus = String(item.status || '').trim().toLowerCase();
        const isActive =
            item.is_active !== false &&
            item.isActive !== false &&
            item.is_published !== false &&
            item.isPublished !== false &&
            rawStatus !== 'inactive' &&
            rawStatus !== 'draft' &&
            rawStatus !== 'unpublished' &&
            rawStatus !== 'archived';

        const rawOrder =
            item.display_order ??
            item.displayOrder ??
            item.sort_order ??
            item.sortOrder ??
            item.order;
        const displayOrder = Number.isFinite(Number(rawOrder))
            ? Number(rawOrder)
            : index;

        const id = String(item._id || item.id || `on-ground-media-${index}`);
        const title = String(
            item.title || item.heading || item.name || item.label || ''
        ).trim();
        const desc = String(
            item.description || item.desc || item.caption || item.subtitle || ''
        ).trim();
        const highlight = String(
            item.highlight ||
                item.badge ||
                item.feature_badge ||
                item.featureBadge ||
                item.tag ||
                ''
        ).trim();

        const { category, categoryLabel, categoryTabLabel } =
            this.resolveCategoryMeta(item);

        const rawSourceType = String(
            item.source_type || item.sourceType || ''
        )
            .trim()
            .toLowerCase();
        const isExplicitUpload =
            rawSourceType === 'upload' ||
            rawSourceType === 'uploaded' ||
            rawSourceType === 'file' ||
            rawSourceType === 'local';

        const rawMediaType = String(
            item.media_type || item.mediaType || item.type || ''
        )
            .trim()
            .toLowerCase();
        const mimeType = String(
            item.mime_type || item.mimeType || item.file_type || item.fileType || ''
        )
            .trim()
            .toLowerCase();

        const rawMediaUrl = extractUrlString(
            item.uploaded_media_url ||
                item.uploadedMediaUrl ||
                item.media_url ||
                item.mediaUrl ||
                item.file_url ||
                item.fileUrl ||
                item.url ||
                item.src
        );

        const rawImageUrl = extractUrlString(
            item.image_url ||
                item.imageUrl ||
                item.uploaded_image_url ||
                item.uploadedImageUrl ||
                item.image ||
                item.photo_url ||
                item.photoUrl ||
                item.photo
        );

        const rawVideoUrl = extractUrlString(
            item.video_url ||
                item.videoUrl ||
                item.uploaded_video_url ||
                item.uploadedVideoUrl ||
                item.video_file ||
                item.videoFile ||
                item.video
        );

        const rawThumbUrl = extractUrlString(
            item.thumbnail_url ||
                item.thumbnailUrl ||
                item.thumbnail ||
                item.poster_url ||
                item.posterUrl ||
                item.poster ||
                item.preview_image ||
                item.previewImage
        );

        // Determine whether this record is an image or a video
        let mediaType = 'image';
        if (
            rawMediaType === 'video' ||
            rawMediaType === 'reel' ||
            rawMediaType === 'short' ||
            mimeType.startsWith('video/')
        ) {
            mediaType = 'video';
        } else if (
            rawMediaType === 'image' ||
            rawMediaType === 'photo' ||
            rawMediaType === 'picture' ||
            mimeType.startsWith('image/')
        ) {
            mediaType = 'image';
        } else if (rawVideoUrl) {
            mediaType = 'video';
        } else {
            const candidate = rawMediaUrl || rawImageUrl;
            if (hasImageExtension(candidate)) {
                mediaType = 'image';
            } else if (
                hasVideoExtension(candidate) ||
                (!isExplicitUpload && Boolean(extractYouTubeId(candidate))) ||
                isKnownEmbedUrl(candidate)
            ) {
                mediaType = 'video';
            }
        }

        const altText = String(
            item.alt_text ||
                item.altText ||
                title ||
                'Rahber Corporation Showroom Media'
        ).trim();

        if (mediaType === 'video') {
            const videoCandidate = rawVideoUrl || rawMediaUrl;
            if (!videoCandidate) return null;

            const youtubeId = isExplicitUpload
                ? null
                : extractYouTubeId(videoCandidate);
            const isEmbedVideo =
                !isExplicitUpload && !youtubeId && isKnownEmbedUrl(videoCandidate);
            const isDirectVideo = !youtubeId && !isEmbedVideo;
            const videoSrc = isDirectVideo
                ? resolveMediaUrl(videoCandidate)
                : null;

            if (!youtubeId && !isEmbedVideo && !videoSrc) {
                return null;
            }

            let thumbnailUrl = resolveMediaUrl(rawThumbUrl || rawImageUrl) || '';
            if (!thumbnailUrl && youtubeId) {
                thumbnailUrl = `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
            }

            return {
                id,
                _id: id,
                mediaType: 'video',
                title: title || 'Rahber Showroom Media',
                desc,
                altText,
                category,
                categoryLabel,
                categoryTabLabel,
                highlight,
                imageUrl: thumbnailUrl || '',
                thumbnailUrl,
                videoSrc,
                youtubeId,
                isDirectVideo,
                isEmbedVideo,
                embedUrl: isEmbedVideo ? videoCandidate : null,
                displayOrder,
                isActive,
                videoModalData: {
                    _id: id,
                    id,
                    title: title || 'Rahber Showroom Media',
                    description: desc,
                    tag: categoryLabel || highlight,
                    isDirectVideo,
                    videoSrc,
                    youtubeId,
                    isEmbedVideo,
                    embedUrl: isEmbedVideo ? videoCandidate : null,
                    url: videoCandidate,
                    thumbnail: thumbnailUrl || '',
                },
            };
        }

        // Standard image media item
        const imageSource = rawImageUrl || rawMediaUrl || rawThumbUrl;
        const imageUrl = resolveMediaUrl(imageSource);
        if (!imageUrl) return null;

        return {
            id,
            _id: id,
            mediaType: 'image',
            title: title || 'Rahber Showroom Display',
            desc,
            altText,
            category,
            categoryLabel,
            categoryTabLabel,
            highlight,
            imageUrl,
            thumbnailUrl: resolveMediaUrl(rawThumbUrl) || imageUrl,
            videoSrc: null,
            youtubeId: null,
            isDirectVideo: false,
            isEmbedVideo: false,
            embedUrl: null,
            displayOrder,
            isActive,
            videoModalData: null,
        };
    }

    /**
     * Extract media array flexibly across standard API response envelopes.
     */
    extractList(response) {
        const candidates = [
            response?.data?.onGroundMedia,
            response?.data?.on_ground_media,
            response?.data?.mediaItems,
            response?.data?.media_items,
            response?.data?.gallery,
            response?.data?.media,
            response?.data?.items,
            response?.onGroundMedia,
            response?.on_ground_media,
            response?.mediaItems,
            response?.media_items,
            response?.gallery,
            response?.media,
            response?.items,
            Array.isArray(response?.data) ? response.data : null,
            Array.isArray(response) ? response : null,
        ];

        const found = candidates.find((entry) => Array.isArray(entry));
        return found || [];
    }

    /**
     * Fetch active/published on-ground media items sorted by display_order.
     * Returns an empty array (never dummy data) if none are published or if the route
     * has not been provisioned yet (404), and throws on network/server failures so the
     * component can render its error & retry UI.
     *
     * @param {object} params
     * @returns {Promise<Array>}
     */
    async getActiveMedia(params = { active_only: true }) {
        try {
            const response = await apiService.get(
                '/ecommerce/on-ground-media',
                params
            );

            if (response && response.success === false) {
                throw {
                    message:
                        response.message ||
                        'Failed to load showroom media. Please try again.',
                    status: 500,
                };
            }

            const rawList = this.extractList(response);
            if (!Array.isArray(rawList) || rawList.length === 0) {
                return [];
            }

            return rawList
                .map((item, idx) => this.normalizeMediaItem(item, idx))
                .filter((item) => item && item.isActive)
                .sort((a, b) => a.displayOrder - b.displayOrder);
        } catch (error) {
            // If backend endpoint has not been deployed yet (404), return empty list gracefully
            if (error && Number(error.status) === 404) {
                return [];
            }
            console.warn(
                '[OnGroundMediaService] Failed to fetch on-ground media:',
                error?.message || error
            );
            throw error;
        }
    }
}

const onGroundMediaService = new OnGroundMediaService();
export default onGroundMediaService;
